import { useRouter } from 'next/router'
import { useMemo } from 'react'

import { ProductReviewsFilters } from './useProductReviews'

type UseProductReviewFiltersOutput = {
  filters: ProductReviewsFilters
  activeFilterCount: number
  pageNum: number
  updateFilters: (updatedFilters: Record<string, string | string[] | number[] | undefined>) => void
}

export const useProductReviewFilters = (): UseProductReviewFiltersOutput => {
  const router = useRouter()

  const output: UseProductReviewFiltersOutput = useMemo(() => {
    const query: Record<string, string | string[] | undefined> = {
      ...router.query,
    }

    const updateFilters = (
      updatedFilters: Record<string, string | string[] | number[] | undefined>
    ) => {
      const entries = Object.entries(updatedFilters)

      if (entries.length > 0) {
        entries.forEach(([key, value]) => {
          if (typeof value === 'undefined') {
            delete query[key]
          } else if (Array.isArray(value)) {
            if (value.length > 0) {
              query[key] = value.join()
            } else {
              delete query[key]
            }
          } else {
            if (value === '') {
              delete query[key]
            } else {
              query[key] = value
            }
          }
        })
        router.replace({ query }, undefined, { scroll: false, shallow: true })
      } else {
        router.replace({ query: { handle: query.handle } }, undefined, {
          scroll: false,
          shallow: true,
        })
      }
    }

    // default return value
    const result: UseProductReviewFiltersOutput = {
      filters: {},
      activeFilterCount: 0,
      pageNum: 1,
      updateFilters,
    }

    // default filters
    const pageParam = query['page']
    const searchParam = query['search']
    const ratingParam = query['rating']

    // `ProductTag`-specific filters
    const sizeParam = query['size']
    const lengthParam = query['length']
    const weightParam = query['weight']
    const heightParam = query['height']
    const fitParam = query['fit']
    const occupationParam = query['occupation']
    const mediaTypeParam = query['mediaType']

    result.pageNum = pageParam ? Number(pageParam as string) : result.pageNum

    result.filters = {
      search: searchParam ? (searchParam as string) : undefined,
      rating: ratingParam
        ? (ratingParam as string)
            .split(',')
            .map(e => Number(e))
            .filter(e => e !== undefined && 1 <= e && e <= 5)
            .map(e => e.toString())
        : undefined,
      size: sizeParam ? (sizeParam as string).split(',') : undefined,
      length: lengthParam ? (lengthParam as string).split(',') : undefined,
      weight: weightParam ? (weightParam as string).split(',') : undefined,
      height: heightParam ? (heightParam as string).split(',') : undefined,
      fit: fitParam ? (fitParam as string).split(',') : undefined,
      occupation: occupationParam ? (occupationParam as string).split(',') : undefined,
      mediaType: mediaTypeParam ? (mediaTypeParam as string).split(',') : undefined,
    }

    result.activeFilterCount = Object.entries(result.filters).reduce<number>(
      (count, [key, value]) => {
        let selected = 0
        // `searchText` doesn't count as an active filter
        if (key !== 'searchText') {
          if (Array.isArray(value)) {
            selected = value.length
          }
        }
        return count + selected
      },
      0
    )

    return result
  }, [router])

  return output
}
